import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Instagram, Youtube, Music2, Star, Share2, DollarSign, UtensilsCrossed, CalendarHeart,
  Loader2, Check, Heart, Users, MapPin, Sparkles, Radio, ArrowRight, CreditCard,
} from "lucide-react";
import { api } from "../lib/api";
import { demoCopy } from "../lib/demo-copy";
import { useLang } from "../lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298 0 .59.045.87.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);
const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M17.05 12.53c-.02-2.03 1.66-3 1.73-3.05-.94-1.38-2.4-1.57-2.93-1.59-1.25-.13-2.44.73-3.07.73-.63 0-1.61-.71-2.65-.69-1.36.02-2.62.79-3.32 2.01-1.42 2.46-.36 6.1 1.02 8.1.67.98 1.47 2.08 2.51 2.04 1.01-.04 1.39-.65 2.61-.65 1.22 0 1.56.65 2.62.63 1.08-.02 1.77-1 2.43-1.98.77-1.13 1.09-2.22 1.11-2.28-.02-.01-2.13-.82-2.15-3.25zM15.03 6.3c.56-.68.94-1.62.84-2.56-.81.03-1.79.54-2.37 1.22-.52.6-.97 1.56-.85 2.48.9.07 1.82-.46 2.38-1.14z" />
  </svg>
);
const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" {...props}>
    <path fill="#4285F4" d="M22.5 12.2c0-.68-.06-1.36-.18-2.02H12v3.83h5.9a5.05 5.05 0 0 1-2.19 3.31v2.75h3.54c2.07-1.9 3.25-4.71 3.25-7.87z" />
    <path fill="#34A853" d="M12 23c2.94 0 5.42-.97 7.23-2.63l-3.54-2.75c-.98.66-2.24 1.05-3.69 1.05-2.84 0-5.24-1.92-6.1-4.5H2.24v2.84A10.99 10.99 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.9 14.17a6.6 6.6 0 0 1 0-4.34V6.99H2.24a11 11 0 0 0 0 9.02z" />
    <path fill="#EA4335" d="M12 5.33c1.6 0 3.04.55 4.17 1.63l3.13-3.13A10.99 10.99 0 0 0 12 1a10.99 10.99 0 0 0-9.76 5.99L5.9 9.83C6.76 7.25 9.16 5.33 12 5.33z" />
  </svg>
);

const MENU = [
  { name: "Truffle Arancini", price: 12, desc: "Crisp risotto, black truffle, parmesan" },
  { name: "Negroni Sbagliato", price: 14, desc: "Prosecco, Campari, sweet vermouth" },
  { name: "Wagyu Sliders", price: 18, desc: "Trio of aged wagyu, brioche, aioli" },
  { name: "Burrata & Peach", price: 13, desc: "Creamy burrata, grilled peach, basil" },
];

export default function CustomerQR() {
  const { artistId } = useParams();
  const { t } = useLang();
  const q = t.qrp;
  const [artist, setArtist] = useState(null);
  const [venue, setVenue] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [cart, setCart] = useState([]);
  const [tipOpen, setTipOpen] = useState(false);
  const [payStatus, setPayStatus] = useState(null); // null | checking | success | failed
  const [searchParams, setSearchParams] = useSearchParams();

  const menuRef = useRef(null);
  const socialRef = useRef(null);

  const loadReviews = useCallback(() => api.reviews(artistId).then(setReviews), [artistId]);

  useEffect(() => {
    api.qrContext(artistId).then((d) => { setArtist(d.artist); setVenue(d.venue); }).catch(() => {});
    loadReviews();
    api.track({ artist_id: artistId, kind: "qr_scan" });
    api.track({ artist_id: artistId, kind: "profile_visit" });
  }, [artistId, loadReviews]);

  // Handle Stripe return
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setPayStatus("checking");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.tipStatus(sessionId);
        if (res.payment_status === "paid") {
          setPayStatus("success");
          loadReviews();
          setSearchParams({}, { replace: true });
          return;
        }
        if (res.status === "expired") { setPayStatus("failed"); setSearchParams({}, { replace: true }); return; }
      } catch (e) { /* keep polling */ }
      if (attempts >= 6) { setPayStatus("failed"); setSearchParams({}, { replace: true }); return; }
      setTimeout(poll, 1800);
    };
    poll();
  }, [searchParams, setSearchParams, loadReviews]);

  const follow = (platform, url) => {
    api.track({ artist_id: artistId, kind: "social_click", platform });
    api.track({ artist_id: artistId, kind: "follow", platform });
    toast.success(`${q.opening} ${platform}`);
    window.open(url, "_blank");
  };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: artist.name, url }); } catch {} }
    else { navigator.clipboard.writeText(url); toast.success(q.linkCopied); }
  };
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const cartTotal = cart.reduce((s, i) => s + i.price, 0);

  if (!artist) return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><Loader2 className="animate-spin gold" size={32} /></div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-28 overflow-x-hidden">
      {/* Payment result overlay */}
      <AnimatePresence>
        {payStatus && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center px-6" data-testid="pay-overlay">
            {payStatus === "checking" && (
              <div className="text-center"><Loader2 className="animate-spin gold mx-auto mb-4" size={40} /><p className="text-neutral-300">{q.confirming}</p></div>
            )}
            {payStatus === "success" && (
              <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="text-center" data-testid="pay-success">
                <div className="w-20 h-20 rounded-full bg-gold flex items-center justify-center mx-auto mb-6 gold-glow"><Check size={40} className="text-black" /></div>
                <h2 className="font-serif-lux text-4xl font-light mb-2">{q.thankYou}</h2>
                <p className="text-neutral-400 mb-8">{q.thankSub} {artist.name}.</p>
                <button data-testid="pay-close" onClick={() => setPayStatus(null)} className="btn-gold px-8 py-3 rounded-sm text-sm font-semibold">{q.backToShow}</button>
              </motion.div>
            )}
            {payStatus === "failed" && (
              <div className="text-center" data-testid="pay-failed">
                <p className="font-serif-lux text-3xl font-light mb-3">{q.payFailed}</p>
                <p className="text-neutral-400 mb-6">{q.payFailedSub}</p>
                <button onClick={() => setPayStatus(null)} className="btn-ghost-lux px-8 py-3 rounded-sm text-sm">{q.close}</button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <div className="relative h-[68vh] max-h-[620px]">
        <motion.img
          src={artist.photo} alt={artist.name}
          initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-black/30" />
        <Link to="/" className="absolute top-4 left-4 glass px-3 py-1.5 rounded-sm text-xs" data-testid="qr-home-link">← Buskers.</Link>
        <button onClick={share} data-testid="qr-share" className="absolute top-4 right-4 glass w-9 h-9 rounded-sm flex items-center justify-center"><Share2 size={16} /></button>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
          className="absolute bottom-7 left-5 right-5">
          <div className="inline-flex items-center gap-2 glass border border-gold/40 px-3 py-1.5 rounded-full mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.25em] gold flex items-center gap-1"><Radio size={11} /> {q.live}</span>
          </div>
          <h1 className="font-serif-lux text-5xl font-light leading-none">{artist.name}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-sm text-neutral-300">
            <span className="gold">{artist.style}</span>
            <span className="text-neutral-600">·</span>
            <span className="flex items-center gap-1"><Star size={13} className="gold fill-current" /> {artist.rating}</span>
            <span className="text-neutral-600">·</span>
            <span className="flex items-center gap-1"><Users size={13} /> {(artist.followers / 1000).toFixed(1)}k</span>
          </div>
          {venue && (
            <p className="flex items-center gap-1.5 mt-2 text-sm text-neutral-400" data-testid="qr-venue">
              <MapPin size={14} className="gold" /> {q.liveAt} <span className="text-white">{venue.name}</span> · {venue.city}
            </p>
          )}
        </motion.div>
      </div>

      <div className="px-5 max-w-lg mx-auto space-y-8 -mt-1">
        <p className="text-sm text-neutral-400 leading-relaxed">{artist.bio}</p>

        {/* 4 main CTAs */}
        <div className="grid grid-cols-2 gap-3" data-testid="qr-cta-grid">
          <CTA testid="cta-order" primary icon={<UtensilsCrossed size={20} />} label={q.ctaOrder} sub={q.ctaOrderSub} onClick={() => scrollTo(menuRef)} />
          <CTA testid="cta-follow" icon={<Heart size={20} />} label={q.ctaFollow} sub={q.ctaFollowSub} onClick={() => scrollTo(socialRef)} />
          <BookDialog artist={artist} venue={venue}>
            <CTA testid="cta-book" icon={<CalendarHeart size={20} />} label={q.ctaBook} sub={q.ctaBookSub} />
          </BookDialog>
          <CTA testid="cta-tip" icon={<DollarSign size={20} />} label={q.ctaTip} sub={q.ctaTipSub} onClick={() => setTipOpen(true)} />
        </div>

        {/* Socials */}
        <div ref={socialRef} className="scroll-mt-4">
          <p className="text-xs uppercase tracking-[0.3em] gold mb-3">{q.followTitle}</p>
          <div className="grid grid-cols-2 gap-3">
            <FollowBtn testid="follow-instagram" icon={<Instagram size={18} />} label="Instagram" onClick={() => follow("instagram", artist.socials.instagram)} />
            <FollowBtn testid="follow-tiktok" icon={<TikTok />} label="TikTok" onClick={() => follow("tiktok", artist.socials.tiktok)} />
            <FollowBtn testid="follow-youtube" icon={<Youtube size={18} />} label="YouTube" onClick={() => follow("youtube", artist.socials.youtube)} />
            <FollowBtn testid="follow-spotify" icon={<Music2 size={18} />} label="Spotify" onClick={() => follow("spotify", artist.socials.spotify)} />
          </div>
        </div>

        {/* Menu */}
        <div ref={menuRef} className="scroll-mt-4">
          <p className="text-xs uppercase tracking-[0.3em] gold mb-3 flex items-center gap-2"><UtensilsCrossed size={14} /> {q.orderTitle}</p>
          <div className="card-lux divide-y divide-white/10">
            {MENU.map((m) => (
              <div key={m.name} className="flex items-center justify-between p-4">
                <div><p className="text-sm">{m.name}</p><p className="text-xs text-neutral-500">{m.desc}</p></div>
                <div className="flex items-center gap-3">
                  <span className="gold text-sm">£{m.price}</span>
                  <button data-testid={`add-${m.name}`} onClick={() => { setCart([...cart, m]); toast.success(`${m.name} ${q.added}`); }}
                    className="btn-ghost-lux w-7 h-7 rounded-sm text-sm">+</button>
                </div>
              </div>
            ))}
          </div>
          {cart.length > 0 && (
            <button data-testid="place-order" onClick={() => { toast.success(`${q.orderPlaced} · £${cartTotal} ${q.demo}`); setCart([]); }}
              className="btn-gold w-full mt-3 py-3 rounded-sm text-sm font-semibold">{q.placeOrder} · £{cartTotal} {q.demo}</button>
          )}
        </div>

        {/* Reviews */}
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-3">{q.reviewsTitle}</p>
          <div className="card-lux divide-y divide-white/10" data-testid="qr-reviews">
            {reviews.length === 0 && <p className="p-4 text-sm text-neutral-500">{q.noReviews}</p>}
            {reviews.slice(0, 3).map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-center gap-1 mb-1">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={11} className="gold fill-current" />)}</div>
                <p className="text-sm text-neutral-300">{r.text}</p>
                <p className="text-xs text-neutral-600 mt-1">— {r.guest_name}</p>
              </div>
            ))}
          </div>
          <ReviewDialog artistId={artistId} onDone={loadReviews} />
        </div>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 glass border-t border-white/10 p-4 z-50">
        <div className="max-w-lg mx-auto">
          <button data-testid="footer-tip" onClick={() => setTipOpen(true)}
            className="btn-gold w-full py-3.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 gold-glow">
            <Heart size={16} /> {q.tipShort} {artist.name.split(" ")[0]}
          </button>
          <p className="text-center text-[10px] text-neutral-500 mt-2 flex items-center justify-center gap-2">
            {q.payWith} <AppleIcon /> Apple Pay <GoogleIcon /> Google Pay <CreditCard size={12} /> {q.secured}
          </p>
        </div>
      </div>

      <TipDialog artist={artist} open={tipOpen} setOpen={setTipOpen} />
    </div>
  );
}

const CTA = ({ icon, label, sub, onClick, primary, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`text-left p-5 rounded-sm transition-all border flex flex-col gap-3 h-full ${
      primary ? "bg-gold text-black border-gold hover:bg-[#E5C158] gold-glow" : "card-lux hover:border-white/25 text-white"
    }`}>
    <span className={primary ? "text-black" : "gold"}>{icon}</span>
    <div>
      <p className="text-sm font-semibold leading-tight">{label}</p>
      <p className={`text-xs mt-1 ${primary ? "text-black/60" : "text-neutral-500"}`}>{sub}</p>
    </div>
  </button>
);

const FollowBtn = ({ icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="btn-ghost-lux flex items-center justify-center gap-2 py-3 rounded-sm text-sm">
    {icon} {label}
  </button>
);

function TipDialog({ artist, open, setOpen }) {
  const { t, lang } = useLang();
  const q = t.qrp;
  const [option, setOption] = useState("10");
  const [custom, setCustom] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const amount = option === "custom" ? parseFloat(custom || 0) : parseFloat(option);

  const pay = async () => {
    if (option === "custom" && (!custom || amount < 1)) { toast.error(q.enterMin); return; }
    setLoading(true);
    try {
      const payload = {
        artist_id: artist.id, origin_url: window.location.origin,
        guest_name: "Guest", message: msg,
        ...(option === "custom" ? { custom_amount: amount } : { tip_option: option }),
      };
      const res = await api.tipCheckout(payload);
      window.location.href = res.url; // Local demo confirmation; no payment provider is contacted.
    } catch (e) {
      toast.error(q.couldNotPay);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-serif-lux text-2xl font-light">{q.tipShort} {artist.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-neutral-300">{(demoCopy[lang] || demoCopy.en).tipNote}</p>
          <div className="grid grid-cols-4 gap-2">
            {["5", "10", "20"].map((v) => (
              <button key={v} data-testid={`tip-${v}`} onClick={() => setOption(v)}
                className={`py-3.5 rounded-sm text-sm font-semibold border ${option === v ? "bg-gold text-black border-gold" : "btn-ghost-lux"}`}>£{v}</button>
            ))}
            <button data-testid="tip-custom" onClick={() => setOption("custom")}
              className={`py-3.5 rounded-sm text-sm font-semibold border ${option === "custom" ? "bg-gold text-black border-gold" : "btn-ghost-lux"}`}>{q.custom}</button>
          </div>
          {option === "custom" && (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 gold">£</span>
              <input data-testid="tip-custom-amount" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={q.enterAmount}
                className="w-full bg-transparent border border-white/20 rounded-sm pl-7 pr-3 py-2.5 text-sm focus:border-gold outline-none" />
            </div>
          )}
          <input data-testid="tip-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={q.msgPlaceholder}
            className="w-full bg-transparent border border-white/20 rounded-sm px-3 py-2.5 text-sm focus:border-gold outline-none" />

          <button data-testid="tip-pay" onClick={pay} disabled={loading}
            className="btn-gold w-full py-3.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <>{q.continuePay} · £{amount || 0} <ArrowRight size={15} /></>}
          </button>

        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookDialog({ artist, venue, children }) {
  const { t } = useLang();
  const q = t.qrp;
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const submit = async () => {
    if (!date) { toast.error(q.pickDate); return; }
    await api.createBooking({ venue_id: venue?.id || "v1", artist_id: artist.id, date, shift: "Private Event", fee: artist.hourly_rate * 3 });
    toast.success(q.privateReqSent);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-serif-lux text-2xl font-light">{q.bookPre} {artist.name} {q.bookSuf}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <input data-testid="private-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full bg-transparent border border-white/20 rounded-sm px-3 py-2.5 text-sm focus:border-gold outline-none" />
          <p className="text-sm text-neutral-400">{q.from} <span className="gold">£{artist.hourly_rate * 3}</span> · {q.privateSet}</p>
          <button data-testid="confirm-private" onClick={submit} className="btn-gold w-full py-3 rounded-sm text-sm font-semibold">{q.sendRequest}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ artistId, onDone }) {
  const { t } = useLang();
  const q = t.qrp;
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const submit = async () => {
    if (!text) { toast.error(q.writeReview); return; }
    await api.createReview({ artist_id: artistId, rating, text, guest_name: "Guest" });
    toast.success(q.reviewPosted);
    setOpen(false); setText(""); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-review" className="btn-ghost-lux w-full mt-3 py-2.5 rounded-sm text-sm">{q.leaveReview}</button>
      </DialogTrigger>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-serif-lux text-2xl font-light">{q.reviewTitle}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)}>
                <Star size={26} className={s <= rating ? "gold fill-current" : "text-neutral-600"} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={q.reviewPlaceholder}
            className="w-full bg-transparent border border-white/20 rounded-sm px-3 py-2 text-sm focus:border-gold outline-none" />
          <button data-testid="submit-review" onClick={submit} className="btn-gold w-full py-3 rounded-sm text-sm font-semibold">{q.postReview}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
